// App-side delegate for `@heidi/commerce/data` (D2 batch 2, ARCFE-973). The
// query moved; this file keeps the name, the `enabled` argument shape, and
// composes the two app-resolved reads the package hook takes as inputs.
import {
  applyGamifyDevOverride,
  gamifyDevOverrideAtom,
} from '@/features/billing-dev/gamify-overrides';
import { logger } from '@/utils/logger/logger';
import type { GamifyTrialExtensionResponse } from '@heidi/commerce/contracts';
import {
  invalidateGamifyTrialExtension as invalidateGamifyTrialExtensionCore,
  useQueryGamifyTrialExtension as useQueryGamifyTrialExtensionCore,
} from '@heidi/commerce/data';
import { sanitizeError } from '@heidi/runtime/logger';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { invalidateCreditAllowance } from './useQueryCreditAllowance';
import { useQueryUser } from './useQueryUser';
import { invalidateCurrentUserBillingInfo } from './useQueryUserBillingInfo';

// Definition moved to `@heidi/commerce/contracts` (C-series, ARCFE-971).
// import + re-export: this module uses the name locally, and a bare
// `export { X } from '...'` does not bind it here.
import { GET_GAMIFY_TRIAL_EXTENSION } from '@heidi/commerce/contracts';

export { GET_GAMIFY_TRIAL_EXTENSION };

// The extension moves the trial end in Stripe, and billing info and the
// allowance `cycle_end` only follow once the subscription webhook lands.
const TRIAL_DATES_REFRESH_DELAY_MS = 3_000;

// One refresh however many surfaces observe the extension at once.
let pendingTrialDatesRefresh: ReturnType<typeof setTimeout> | undefined;

function scheduleTrialDatesRefresh() {
  if (pendingTrialDatesRefresh !== undefined) return;
  pendingTrialDatesRefresh = setTimeout(() => {
    pendingTrialDatesRefresh = undefined;
    Promise.all([invalidateCurrentUserBillingInfo(), invalidateCreditAllowance()]).catch(
      (error: unknown) => {
        logger.error('Failed to refresh trial dates after trial extension', {
          error: sanitizeError(error),
        });
      }
    );
  }, TRIAL_DATES_REFRESH_DELAY_MS);
}

/** Refreshes the trial dates when this session sees the extension granted. */
function useRefreshTrialDatesOnExtension(trialExtended: boolean | undefined) {
  const previous = useRef(trialExtended);
  useEffect(() => {
    if (previous.current === false && trialExtended === true) {
      scheduleTrialDatesRefresh();
    }
    previous.current = trialExtended;
  }, [trialExtended]);
}

export function useQueryGamifyTrialExtension(enabled = true) {
  const { data: user } = useQueryUser();
  const devOverride = useAtomValue(gamifyDevOverrideAtom);
  const select = useCallback(
    (data: GamifyTrialExtensionResponse) => applyGamifyDevOverride(data, devOverride),
    [devOverride]
  );

  const query = useQueryGamifyTrialExtensionCore({ hasUser: !!user, enabled, select });
  useRefreshTrialDatesOnExtension(query.data?.trial_extended);
  return query;
}

/**
 * Both invalidations still run CONCURRENTLY under one `Promise.all`, and the
 * caller still awaits both. Billing stays app-side until the D3 transfer, so
 * the pair is composed here rather than inside the package.
 */
export const invalidateGamifyTrialExtension = async () => {
  await Promise.all([
    invalidateGamifyTrialExtensionCore(),
    invalidateCurrentUserBillingInfo(),
  ]);
};
